/*
 * VideoScreen.jsx — ARGO · Full screen video player
 *
 * - Không dùng native controls → dùng UI custom để bắt được sự kiện
 * - Video muted để autoplay, nút unmute nhấp nháy ở dưới màn hình
 * - Nút X góc trên do code render → onBack hoạt động đúng
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const BLUE = '#00C2FF';

/* ── Keyframes ── */
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const pulse  = keyframes`
  0%, 100% {
    transform: scale(1);   
    box-shadow: 0 0 0 0 rgba(0,194,255,.6); 
  }
  50% {
    transform: scale(1.08); 
    box-shadow: 0 0 0 12px rgba(0,194,255,0); 
  }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;

/* ── Styled ── */
const Container = styled.div`
  position: fixed; 
  inset: 0; 
  background: #000; 
  z-index: 100;
  display: flex; 
  align-items: center; 
  justify-content: center;
  animation: ${fadeIn} .3s ease both;
`;

const VideoEl = styled.video`
  width: 100%; height: 100%; object-fit: contain;
  /* Không dùng controls — UI custom bên dưới */
`;

/* Nút X — do code render, bắt được onBack */
const CloseBtn = styled.button`
  position: absolute;
  top: max(20px, env(safe-area-inset-top));
  right: 20px;
  width: 44px; 
  height: 44px; 
  border-radius: 50%;
  border: 0.5px solid rgba(255,255,255,.25);
  background: rgba(0,0,0,.6); 
  backdrop-filter: blur(12px);
  color: #fff; 
  cursor: pointer; 
  display: grid; 
  place-items: center;
  z-index: 101; 
  font-size: 20px; 
  line-height: 1;
  transition: background .15s;
  &:active { 
    background: rgba(255,255,255,.15); 
  }
`;

/* Nút unmute — nhấp nháy, nằm trong BottomBar */
const UnmuteBtn = styled.button`
  display: inline-flex; 
  align-items: center; 
  gap: 10px;
  padding: 12px 22px; 
  border-radius: 999px;
  border: 1.5px solid ${BLUE};
  background: rgba(0, 0, 0, .65); 
  backdrop-filter: blur(14px);
  color: #fff; 
  cursor: pointer;
  font-size: 14px; 
  font-weight: 600;
  font-family: 'Exo 2', sans-serif;
  letter-spacing: .04em;
  z-index: 101;
  animation: ${pulse} 1.8s ease-in-out infinite;
  transition: background .15s;
  &:active { 
    background: rgba(0,194,255,.25); 
    animation: none; 
  }
  white-space: nowrap;
`;

/* Wrapper ngang hàng 2 nút */
const BottomBar = styled.div`
  position: absolute;
  bottom: max(40px, env(safe-area-inset-bottom) + 20px);
  left: 0; right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 101;
  padding: 0 24px;
`;

const UnmuteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <line x1="23" y1="9" x2="17" y2="15"/>
    <line x1="17" y1="9" x2="23" y2="15"/>
  </svg>
);

const InfoBar = styled.div`
  position: absolute; 
  bottom: 0; 
  left: 0; 
  right: 0;
  padding: 20px 20px max(24px, env(safe-area-inset-bottom));
  background: linear-gradient(to top, rgba(0,0,0,.8), transparent);
  pointer-events: none;
`;

const InfoTag   = styled.div`
  font-size:11px;
  font-weight:600;
  letter-spacing:.14em;
  text-transform:uppercase;
  color:${BLUE};
  margin-bottom:4px;
  font-family:'Exo 2',sans-serif;
`;

const InfoTitle = styled.div`
  font-family:'Exo 2',sans-serif;
  font-weight:700;
  font-size:18px;
  color:#fff;
  margin-bottom:4px;
`;

const InfoBody  = styled.div`
  font-size:13px;
  color:rgba(255,255,255,.7);
  line-height:1.5;
`;

const Spinner = styled.div`
  position:absolute; 
  inset:0; 
  display:flex; 
  align-items:center; 
  justify-content:center;
  &::after {
    content:""; 
    width:40px; 
    height:40px; 
    border-radius:50%;
    border:3px solid rgba(0,194,255,.2); 
    border-top-color:${BLUE};
    animation: ${spin} .8s linear infinite;
  }
`;

/* ── Component ── */
export default function VideoScreen({ product, onBack, onSuccess }) {

  const videoRef            = useRef(null);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ]     = useState(false);
  const [ muted, setMuted ]     = useState(true);

  /* Autoplay khi src thay đổi */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    
    setLoading(true);
    setError(false);
    setMuted(true);
    video.muted = true;
    video.load();
    video.play().catch(err => {
      if (err.name !== 'NotAllowedError') {
        setError(true);
        setLoading(false);
      }
    });
  }, [product?.videoSrc]);

  /* Unmute handler */
  const handleUnmute = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.muted = false;
    setMuted(false);
    /* Đảm bảo đang play */
    video.play().catch(() => {});
  }, []);

  if (!product?.videoSrc) { 
    onBack?.(); 
    return null; 
  }

  return (
    <Container>
      {loading && !error && <Spinner />}

      <VideoEl
        ref={videoRef}
        src={product.videoSrc}
        muted
        autoPlay
        playsInline
        crossOrigin="anonymous"
        onLoadedData={() => setLoading(false)}
        onCanPlay={() => setLoading(false)}
        onEnded={() => {
          /* Chuyển sang SuccessScreen thay vì hiện nút Catalog */
          onSuccess?.();
        }}
        onError={() => { 
          setLoading(false); 
          setError(true); 
        }}
      />

      {/* Nút X — custom, bắt được onBack */}
      <CloseBtn onClick={onBack} aria-label="Đóng">✕</CloseBtn>

      {/* BottomBar — chỉ hiện khi muted */}
      {!loading && !error && muted && (
        <BottomBar>
          <UnmuteBtn onClick={handleUnmute}>
            <UnmuteIcon />
            Bật âm thanh
          </UnmuteBtn>
        </BottomBar>
      )}

      {/* Info bar — chỉ hiện khi đã unmute hoặc không có nút unmute */}
      {!loading && !error && !muted && product.info?.title && (
        <InfoBar>
          {product.info.tag   && <InfoTag>{product.info.tag}</InfoTag>}
          {product.info.title && <InfoTitle>{product.info.title}</InfoTitle>}
          {product.info.body  && <InfoBody>{product.info.body}</InfoBody>}
        </InfoBar>
      )}

      {error && (
        <div style={{
          position:'absolute',inset:0,
          display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:16
        }}>
          <p style={{color:'rgba(255,255,255,.6)',fontSize:14}}>
            Không thể tải video
          </p>
          <button onClick={onBack} style={{
            padding:'10px 20px',
            borderRadius:12,
            border:`0.5px solid ${BLUE}`,
            background:'transparent',
            color:'#fff',
            cursor:'pointer',
            fontSize:14
          }}>
            Quay lại
          </button>
        </div>
      )}
    </Container>
  )
};
